import { AppointmentFrequencyLimit, UserMemberCardInfo } from '@/types/member';
import { StoreRef, TimeLimitEnabled, TimeRule, UseStoreScopeType } from '@/types/member-card';
import { PaymentType } from '@/types/common';

export type MemberCardRuleSource = {
  use_store_scope_type?: UseStoreScopeType;
  use_stores?: StoreRef[];
  time_limit_enabled?: TimeLimitEnabled;
  time_rules?: TimeRule[];
  book_limit_type?: number;
  book_limit?: number;
  book_limit_booked?: number;
};

const WEEKDAY_LABELS = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];

function formatWeekdays(weekdays: number[]): string {
  const sorted = [...weekdays].sort((a, b) => a - b);
  return sorted.map((d) => WEEKDAY_LABELS[d] || `周${d}`).join('、');
}

export function formatUseStore(card: MemberCardRuleSource): string {
  if (card.use_store_scope_type === UseStoreScopeType.All || !card.use_store_scope_type) {
    return '全部门店';
  }
  const names = card.use_stores?.map((s) => s.store_name).filter(Boolean);
  return names?.length ? names.join('、') : '指定门店';
}

export function formatTimeRules(card: MemberCardRuleSource): string {
  const enabled =
    card.time_limit_enabled === TimeLimitEnabled.Enabled && (card.time_rules?.length ?? 0) > 0;
  if (!enabled || !card.time_rules?.length) {
    return '不限制';
  }
  return card.time_rules
    .map((rule) => {
      const days = rule.weekdays?.length ? formatWeekdays(rule.weekdays) : '';
      const range = rule.start_time && rule.end_time ? `${rule.start_time}-${rule.end_time}` : '';
      const line = [days, range].filter(Boolean).join(' ');
      return line ? `${line};` : '';
    })
    .filter(Boolean)
    .join('\n');
}

/** 是否为期限卡 */
export function isPeriodCard(card: UserMemberCardInfo): boolean {
  return card.member_card_info?.payment_type === PaymentType.DAY;
}

/** 剩余卡次展示：期限卡不展示卡次，显示为短横线 */
export function formatRemainCardNum(card: UserMemberCardInfo): string {
  if (isPeriodCard(card)) {
    return '-';
  }
  return `${card.remain_card_num} / ${card.total_card_num}`;
}

type FormatBookLimitOptions = {
  showBooked?: boolean;
};

export function formatBookLimit(
  card: MemberCardRuleSource,
  options?: FormatBookLimitOptions,
): string {
  const showBooked = options?.showBooked ?? true;
  const type = card.book_limit_type ?? AppointmentFrequencyLimit.NO_LIMIT;
  const limit = card.book_limit;
  const booked = card.book_limit_booked ?? 0;
  if (type === AppointmentFrequencyLimit.NO_LIMIT || !limit) {
    return '不限制';
  }
  if (type === AppointmentFrequencyLimit.LIMIT_DAILY) {
    return showBooked ? `每天最多${limit}节，今天已约${booked}节` : `每天最多${limit}节`;
  }
  if (type === AppointmentFrequencyLimit.LIMIT_WEEKLY) {
    return showBooked ? `每周最多${limit}节，本周已约${booked}节` : `每周最多${limit}节`;
  }
  if (type === AppointmentFrequencyLimit.LIMIT_MONTHLY) {
    return showBooked ? `每月最多${limit}节，本月已约${booked}节` : `每月最多${limit}节`;
  }
  return '不限制';
}
